import Vue from 'vue'
import ScanSignDialog from '@/pages/orders_new/components/Step1/components/scanSignDialog/index.vue'

/**
 * 命令式打开扫码签约弹窗
 * @param {Object} params 初始参数：{ signType, qrCodeInfo }
 * @param {Object} options 回调：{ onRefresh(payload, done), onClosed() }
 * @returns 控制器：{ close(), refresh(), update(nextParams), open(openParams) }
 */
export function openScanSignDialog(paramsOrOptions, maybeOptions = {}) {
  const container = document.createElement('div')
  document.body.appendChild(container)

  let params = null
  let options = {}

  // 兼容处理：智能识别参数
  // 仅当参数对象只包含 options 相关的已知字段时，才将其视为 options；
  // 否则，只要包含其他任意字段，都优先视为 params（业务参数）。
  const OPTIONS_KEYS = ['onRefresh', 'onClosed']

  const isOptions = (arg) => {
    if (!arg || typeof arg !== 'object') return false
    const keys = Object.keys(arg)
    // 空对象 {} 默认为 options（兼容旧习惯，先获取实例再 open）
    if (keys.length === 0) return true
    // 检查是否所有 key 都在白名单中
    return keys.every((k) => OPTIONS_KEYS.includes(k))
  }

  if (arguments.length === 1) {
    // 如果看起来像 options，就当 options 处理
    if (isOptions(paramsOrOptions)) {
      params = null
      options = paramsOrOptions || {}
    } else {
      // 否则认为是 params
      params = paramsOrOptions
      options = {}
    }
  } else {
    params = paramsOrOptions || null
    options = maybeOptions || {}
  }

  const handleRefresh = (payload, done) => {
    if (typeof options.onRefresh === 'function') {
      options.onRefresh(payload, done)
    } else {
      typeof done === 'function' && done()
    }
  }

  let app = null

  const handleClosed = () => {
    // 保持实例不销毁，以便后续可再次调用 open 重新打开弹窗
    if (typeof options.onClosed === 'function') {
      options.onClosed()
    }
  }

  app = new Vue({
    render(h) {
      return h(ScanSignDialog, {
        ref: 'comp',
        on: {
          refresh: handleRefresh,
          closed: handleClosed,
        },
      })
    },
    mounted() {
      // 如果初始化时传入了参数，自动打开弹窗
      if (params) {
        this.$nextTick(() => {
          const { comp } = this.$refs
          if (comp && typeof comp.showDialog === 'function') {
            comp.showDialog(params)
          }
        })
      }
    },
  }).$mount(container)

  const getInstance = () => {
    return app && app.$refs && app.$refs.comp
  }

  const open = (openParams) => {
    Vue.nextTick(() => {
      const instance = getInstance()
      if (instance && typeof instance.showDialog === 'function') {
        instance.showDialog(openParams || {})
      }
    })
  }

  const destroy = () => {
    if (app) {
      app.$destroy()
      app = null
    }
    if (container && container.parentNode) {
      container.parentNode.removeChild(container)
    }
  }

  return {
    open: (openParams) => {
      // 如果调用 open 时传入了新参数，则使用新参数
      if (openParams) {
        open(openParams)
      } else if (params) {
        // 如果没有传参，但初始化有参数（可能是关闭后重新打开），尝试复用初始参数
        open(params)
      } else {
        // 尝试无参打开（取决于组件是否支持）
        open()
      }
    },
    close: () => {
      const instance = getInstance()
      if (instance && typeof instance.close === 'function') {
        instance.close()
      } else {
        handleClosed()
      }
    },
    refresh: () => {
      const instance = getInstance()
      if (instance && typeof instance.refreshSignStatus === 'function') {
        return instance.refreshSignStatus()
      }
    },
    update: (nextParams) => {
      const instance = getInstance()
      if (instance && typeof instance.showDialog === 'function') {
        instance.showDialog(nextParams)
      }
    },
    destroy, // 暴露销毁方法
  }
}
